// src/sot/primitives/TextField56.jsx
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import Card from "@/components/ui/Card.jsx";
import { type as T } from "@/sot/tokens/typography.js";

export default function TextField56({
  id,
  label = "",
  value,
  onChange,
  defaultValue = "",
  placeholder = "",
  readOnly = false,
  name,
  type = "text",
  autoComplete,
  inputMode,
  disabled = false,
  clearable = false,
  onClear,
  clearLabel,
  inputRef: externalInputRef,
  onFocus,
  onBlur,
  autoCapitalize = "none",
  autoCorrect = "off",
  trailingIcon, // optional icon (e.g. verified) — only shows when there is a value
  leadingIcon,
  leadingIconMask = false,
  leadingSlot = null,
  trailingSlot = null,
  trailingSlotFullHeight = false,
  as = "input",
  prominent = false,
  elevated = prominent,
  textareaResize = "none",
  textareaAutoGrow = false,
  emptyLabelAsPlaceholder = false,
  alignLabelWithLeading = false,
  triggerLabelLeadingIcon,
  onTriggerClick,
  triggerAriaLabel,
  triggerAriaHaspopup,
  triggerAriaExpanded,
  triggerTrailingInteractive = false,
  triggerProps = {},
  tone = "auth",
  className = "",
  readOnlyPlaceholderStyle = null,
  readOnlyValueStyle = null,
  inputProps = {},
}) {
  const inputId = useMemo(
    () => id || `tf56_${name || Math.random().toString(36).slice(2)}`,
    [id, name]
  );
  const isControlled = typeof value !== "undefined";

  const [inner, setInner] = useState(defaultValue ?? "");
  const currentVal = isControlled ? value ?? "" : inner;
  const hasValue = String(currentVal ?? "").length > 0;

  const inputRef = useRef(null);
  const setInputRef = React.useCallback((node) => {
    inputRef.current = node;
    if (typeof externalInputRef === "function") externalInputRef(node);
    else if (externalInputRef && typeof externalInputRef === "object") externalInputRef.current = node;
  }, [externalInputRef]);
  const isTrigger = as === "button";
  const isMultiline = as === "textarea";
  const usesFixedSingleLineHeight = !isMultiline && !prominent;
  const allowsVerticalTextareaResize = isMultiline && textareaResize === "vertical";
  const showTriggerLabel = Boolean(label) && !(emptyLabelAsPlaceholder && !hasValue);
  const isSurfaceTone = tone === "surface";
  const isGhostTone = tone === "ghost";
  const usesOnSurfaceTokens = isSurfaceTone || isGhostTone;
  const Root = elevated ? Card : "div";
  const fieldBackground = elevated
    ? "rgb(var(--clr-surface))"
    : isGhostTone
    ? "rgb(var(--clr-surface-container-low))"
    : isSurfaceTone
      ? "rgb(var(--clr-surface-cont-highest))"
      : "rgb(var(--afrimed-auth-input-surface))";
  const shouldReconcileBrowserPasswordValue =
    isControlled &&
    !readOnly &&
    String(autoComplete || "").toLowerCase().includes("password");

  useLayoutEffect(() => {
    if (!textareaAutoGrow || !isMultiline || readOnly || !inputRef.current) return;

    const textarea = inputRef.current;
    const requiredHeight = textarea.scrollHeight;

    // Grow only when the saved or newly entered content needs more room. This
    // preserves a user-resized height rather than collapsing it on re-render.
    if (requiredHeight > textarea.clientHeight) {
      textarea.style.height = `${requiredHeight}px`;
    }
  }, [currentVal, isMultiline, readOnly, textareaAutoGrow]);

  const labelStyle = {
    fontSize: `${T.labelMedium.size}px`,
    lineHeight: `${T.labelMedium.line}px`,
    fontWeight: T.labelMedium.weight,
    letterSpacing: "0.5px",
    color: usesOnSurfaceTokens
      ? "rgb(var(--clr-on-surface-variant))"
      : "rgb(var(--afrimed-auth-input-label, 91,107,134))",
  };

  const inputStyle = {
    fontSize: `${T.bodyLarge.size}px`,
    lineHeight: `${T.bodyLarge.line}px`,
    fontWeight: T.bodyLarge.weight,
    letterSpacing: "0.5px",
    color: usesOnSurfaceTokens
      ? "rgb(var(--clr-on-surface))"
      : "rgb(var(--afrimed-auth-input-text, 4,8,18))",
    WebkitTextSizeAdjust: "100%",
    textAlign: "left",
  };

  const commit = (next) => {
    if (isControlled) {
      if (typeof onChange === "function") onChange(next);
      return;
    }
    setInner(next);
  };

  const handleClear = () => {
    commit("");
    onClear?.();
    if (inputRef.current) {
      inputRef.current.value = "";
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const reconcileBrowserPasswordValue = React.useCallback(() => {
    if (!shouldReconcileBrowserPasswordValue || !inputRef.current) return;
    const domValue = inputRef.current.value ?? "";
    if (domValue !== String(currentVal ?? "")) {
      commit(domValue);
    }
  }, [currentVal, shouldReconcileBrowserPasswordValue]);

  React.useEffect(() => {
    if (!shouldReconcileBrowserPasswordValue) return undefined;
    const timer = window.setInterval(reconcileBrowserPasswordValue, 200);
    return () => window.clearInterval(timer);
  }, [reconcileBrowserPasswordValue, shouldReconcileBrowserPasswordValue]);

  const leadingVisual = leadingSlot || (leadingIcon ? (
    leadingIconMask ? (
      <span
        aria-hidden="true"
        className="h-[18px] w-[18px] shrink-0 pointer-events-none"
        style={{
          backgroundColor: "rgb(var(--clr-on-surface-variant))",
          WebkitMaskImage: `url("${leadingIcon}")`,
          maskImage: `url("${leadingIcon}")`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: "contain",
          maskSize: "contain",
        }}
      />
    ) : (
      <img
        src={leadingIcon}
        alt=""
        aria-hidden="true"
        className="h-[18px] w-[18px] shrink-0 object-contain pointer-events-none"
      />
    )
  ) : null);
  const triggerLabelLeadingVisual = triggerLabelLeadingIcon ? (
    <img
      src={triggerLabelLeadingIcon}
      alt=""
      aria-hidden="true"
      className="h-[18px] w-[18px] shrink-0 object-contain pointer-events-none"
    />
  ) : null;

  return (
    <Root
      data-afrimed-textfield56-root="true"
      data-afrimed-textfield56-mode={isTrigger ? "trigger" : isMultiline ? "textarea" : "input"}
      className={[
        "tf56-root w-full relative rounded-[12px] px-4",
        usesFixedSingleLineHeight
          ? "h-14 min-h-14 max-h-14 box-border py-1.5"
          : "py-2.5",
        className,
      ].join(" ").trim()}
      style={{
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        overflow: "visible",
        minHeight: prominent ? "146px" : undefined,
        height: prominent && !allowsVerticalTextareaResize ? "146px" : undefined,
        background: fieldBackground,
        border: "none",
        boxShadow: elevated ? undefined : usesOnSurfaceTokens
          ? "inset 0 0 0 1px rgba(var(--clr-outline-variant), 0.9)"
          : "none",
      }}
    >
      <div className="flex flex-col items-start">
        {label && !isTrigger ? (
          <label
            htmlFor={inputId}
            className="tf56-label select-none mb-[1px] cursor-text"
            style={labelStyle}
            onClick={() => inputRef.current?.focus()}
          >
            {label}
          </label>
        ) : null}

        {isTrigger ? (
          <button
            ref={setInputRef}
            type="button"
            aria-label={triggerAriaLabel || label || placeholder || undefined}
            aria-haspopup={triggerAriaHaspopup || undefined}
            aria-expanded={triggerAriaHaspopup ? Boolean(triggerAriaExpanded) : undefined}
            disabled={disabled}
            onClick={onTriggerClick}
            {...triggerProps}
            className="tf56-trigger block w-full relative cursor-pointer border-0 bg-transparent p-0 text-left outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--afrimed-primary))] disabled:cursor-not-allowed disabled:opacity-38"
            style={inputStyle}
          >
            {showTriggerLabel ? <span className="tf56-trigger-label-row flex items-center gap-2 select-none">{triggerLabelLeadingVisual}<span className="tf56-label block select-none mb-[1px]" style={{ ...labelStyle, paddingLeft: alignLabelWithLeading && leadingVisual ? "30px" : 0 }}>{label}</span></span> : null}
            <span className="tf56-value-row flex min-h-[1.75rem] w-full items-center gap-3" style={trailingSlot ? { paddingRight: "2rem" } : undefined}>
              {leadingVisual}
              <span className="min-w-0 flex-1 block truncate" style={hasValue ? inputStyle : { ...inputStyle, color: labelStyle.color }}>{hasValue ? currentVal : emptyLabelAsPlaceholder ? label : placeholder}</span>
            </span>
            {trailingSlot && triggerTrailingInteractive ? (
              <span className="tf56-trigger-trailing-slot absolute inset-y-0 right-0 flex items-center justify-center" aria-hidden="true">
                {trailingSlot}
              </span>
            ) : null}
          </button>
        ) : readOnly ? (
          <div
            className="tf56-value-row flex min-h-[1.75rem] w-full items-center gap-3"
            style={trailingSlot ? { paddingRight: "2.75rem" } : undefined}
          >
            {leadingVisual}
            <span
              className="min-w-0 flex-1 block truncate"
              style={hasValue ? { ...inputStyle, ...(readOnlyValueStyle || {}) } : { ...inputStyle, ...(readOnlyPlaceholderStyle || {}) }}
            >
              {hasValue ? currentVal : placeholder}
            </span>
          </div>
        ) : (
          <div className="tf56-value-row w-full relative">
            {leadingVisual ? <span className="absolute left-0 top-1/2 -translate-y-1/2">{leadingVisual}</span> : null}
            {isMultiline ? <textarea
              ref={setInputRef}
              id={inputId}
              name={name}
              autoComplete={autoComplete}
              disabled={disabled}
              placeholder={placeholder}
              autoCapitalize={autoCapitalize}
              autoCorrect={autoCorrect}
              {...inputProps}
              {...(isControlled
                ? {
                    value: currentVal,
                    onChange: (e) => commit(e.target.value),
                    onFocus: (event) => onFocus?.(event),
                    onBlur: (event) => onBlur?.(event),
                  }
                : {
                    defaultValue: defaultValue ?? "",
                    onChange: (e) => commit(e.target.value),
                    onFocus: (event) => onFocus?.(event),
                    onBlur: (event) => onBlur?.(event),
                  })}
              className={`tf56-input block w-full border-none bg-transparent outline-none focus:ring-0 ${allowsVerticalTextareaResize ? "resize-y" : "min-h-0 resize-none"}`}
              style={{
                ...inputStyle,
                height: prominent && !allowsVerticalTextareaResize ? "104px" : undefined,
                minHeight: prominent && allowsVerticalTextareaResize ? "104px" : 0,
                maxWidth: "100%",
                overflowY: allowsVerticalTextareaResize ? "auto" : undefined,
                padding: 0,
                margin: 0,
              }}
              aria-invalid={false}
              aria-disabled={disabled || undefined}
            /> : <input
              ref={inputRef}
              id={inputId}
              name={name}
              type={type}
              autoComplete={autoComplete}
              inputMode={inputMode}
              disabled={disabled}
              placeholder={placeholder}
              autoCapitalize={autoCapitalize}
              autoCorrect={autoCorrect}
              {...inputProps}
              {...(isControlled
                ? {
                    value: currentVal,
                    onChange: (e) => commit(e.target.value),
                    onInput: (e) => commit(e.currentTarget.value),
                    onFocus: (event) => onFocus?.(event),
                    onBlur: (event) => {
                      reconcileBrowserPasswordValue();
                      onBlur?.(event);
                    },
                }
                : {
                    defaultValue: defaultValue ?? "",
                    onChange: (e) => commit(e.target.value),
                    onFocus: (event) => onFocus?.(event),
                    onBlur: (event) => onBlur?.(event),
                  })}
              className={[
                "tf56-input",
                "block w-full bg-transparent outline-none border-none appearance-none focus:ring-0",
              ].join(" ")}
              style={{
                ...inputStyle,
                "--tf56-autofill-bg":
                  fieldBackground,
                "--tf56-autofill-text":
                  usesOnSurfaceTokens
                    ? "rgb(var(--clr-on-surface))"
                    : "rgb(var(--afrimed-auth-input-text, 4,8,18))",
                padding: 0,
                paddingLeft: leadingVisual ? "1.75rem" : 0,
                margin: 0,
                minHeight: "1.75rem",
                paddingRight:
                  trailingSlot
                    ? "2.75rem"
                    : trailingIcon && hasValue
                    ? "2.25rem" // room for trailing icon
                    : clearable && hasValue
                    ? "3rem"
                    : "0.25rem",
              }}
              enterKeyHint="next"
              aria-invalid={false}
              aria-disabled={disabled || undefined}
            />}

            {trailingSlot && !trailingSlotFullHeight ? <span className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center">{trailingSlot}</span> : null}

            {/* trailing verified icon — only when there is content */}
            {trailingIcon && hasValue && (
              <img
                src={trailingIcon}
                alt=""
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 object-contain pointer-events-none"
              />
            )}

          </div>
        )}
      </div>

      {trailingSlot && trailingSlotFullHeight ? (
        <span className="tf56-trailing-slot-full absolute inset-y-0 right-0 flex items-stretch">
          {trailingSlot}
        </span>
      ) : null}

      {isTrigger && trailingSlot && !triggerTrailingInteractive ? (
        <span className="tf56-trigger-trailing-slot absolute inset-y-0 right-4 flex items-center justify-center" aria-hidden="true">
          {trailingSlot}
        </span>
      ) : null}

      {/* The action is a root sibling so its vertical centring covers the whole
          TextField56 composition, including the label/overline. */}
      {!isTrigger && !readOnly && clearable && hasValue && (
        <div className="tf56-clear-action absolute inset-y-0 right-1 flex items-center justify-center">
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClear}
            aria-label={clearLabel || `Clear ${String(label).replace(/\*+$/, "").trim() || "field"}`}
            className="grid h-11 w-11 place-items-center rounded-lg bg-transparent p-0 hover:bg-[rgba(var(--clr-on-surface),0.08)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[rgb(var(--afrimed-primary))] active:bg-[rgba(var(--clr-on-surface),0.12)]"
          >
            <img src="/icons/doctor/cancel.svg" alt="" className="h-[18px] w-[18px]" />
          </button>
        </div>
      )}
    </Root>
  );
}
